import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import type { MeResponse } from "../../shared/api-types";
import { loginSchema } from "../../shared/schemas";
import { ApiRequestError, api } from "../api/client";
import { Alert, AuthLayout, TextField } from "../components/ui";
import { makeQr, qrSvg } from "../lib/qr";
import { apiFailure, type FieldErrors, validate } from "../pages/forms";
import { Audit, Invites, Support, Users } from "./Accounts";
import { Billing } from "./Billing";
import { ChatInbox, ChatThread } from "./Chat";
import { Dashboard } from "./Dashboard";
import { Ai, Imports, System } from "./Ops";
import { RestaurantDetail, Restaurants } from "./Restaurants";
import { AdminShell } from "./Shell";
import { get } from "./ui";

// Meniova super admin (admin.meniova.com; locally /admin). Separate sign-in: only super admins get a session on
// the admin host, and every /api/admin call is refused (404) for anyone else. This file holds sign-in, two-factor
// setup, the guard and the route tree; the back-office pages are in the files next to it (owner 2026-11-03,
// "Admin nou": business view, restaurant 360°, invoices, system health, support actions — audited on the server).

function useAdminMe() {
  return useQuery({
    queryKey: ["admin", "me"],
    queryFn: () =>
      get<{ user: { id: string; email: string; name: string }; mfaEnabled: boolean; mfaAvailable: boolean }>(
        "/me",
      ),
    retry: false,
  });
}

/**
 * Two-factor authentication setup (LOT 6): on the iPhone the link adds the code to the Passwords app ("Coduri"),
 * which then fills it in at sign-in (owner, 2026-10-27: code only, no passkeys); elsewhere an authenticator app
 * scans the QR or takes the key. A first code confirms it, and the recovery codes are shown once. Until then the
 * admin API refuses everything else.
 */
function MfaSetup({ email }: { email: string }) {
  const qc = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!setup) return;
    void makeQr(setup.uri).then((m) => setQr(`data:image/svg+xml;utf8,${encodeURIComponent(qrSvg(m))}`));
  }, [setup]);
  async function start() {
    setBusy(true);
    setMessage("");
    try {
      setSetup(await api<{ secret: string; uri: string }>("POST", "/admin/mfa/setup"));
    } catch (err) {
      setMessage(apiFailure(err).message);
    } finally {
      setBusy(false);
    }
  }
  async function confirm(e: FormEvent) {
    e.preventDefault();
    const digits = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(digits)) {
      setMessage("Introdu cele 6 cifre din Parole → Coduri → Meniova Admin.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      setCodes(
        (await api<{ recoveryCodes: string[] }>("POST", "/admin/mfa/confirm", { code: digits }))
          .recoveryCodes,
      );
    } catch (err) {
      setMessage(apiFailure(err).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <span className="eyebrow">Meniova · Administrare</span>
        <h1 className="h1">Cod de verificare</h1>
        <p className="muted" style={{ margin: 0 }}>
          Contul {email} are acces la toate restaurantele. Activează un cod de verificare: în aplicația Parole
          de pe iPhone (Parole → Coduri) sau într-o aplicație de autentificare (Google Authenticator,
          1Password…).
        </p>
      </div>
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      {codes ? (
        <div className="stack" style={{ gap: 12 }}>
          <Alert tone="success" title="Autentificarea în doi pași este activă." live>
            Păstrează codurile de mai jos într-un loc sigur: fiecare funcționează o singură dată dacă nu ai
            telefonul la tine. Nu le vei mai vedea.
          </Alert>
          <ul className="verify-names" style={{ fontFamily: "monospace", fontSize: 15 }}>
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void qc.invalidateQueries({ queryKey: ["admin"] })}
          >
            Am salvat codurile — continuă
          </button>
        </div>
      ) : setup ? (
        <form onSubmit={confirm} className="stack" style={{ gap: 16 }}>
          <a className="btn btn-secondary btn-block" href={setup.uri}>
            Adaugă în aplicația Parole (pe acest iPhone)
          </a>
          <span className="caption muted">
            Alege intrarea admin.meniova.com; codul apare apoi în Parole → Coduri. Dacă revii și pagina s-a
            reîncărcat, apasă din nou „Configurează”: cheia rămâne aceeași. De pe alt dispozitiv, scanează
            codul QR.
          </span>
          {qr && (
            <img
              src={qr}
              alt="Cod QR pentru codul de verificare"
              width={200}
              height={200}
              style={{ alignSelf: "center", background: "#fff", padding: 8, borderRadius: 8 }}
            />
          )}
          <span className="caption muted" style={{ overflowWrap: "anywhere" }}>
            Sau introdu cheia manual:{" "}
            <strong style={{ fontFamily: "monospace" }}>
              {setup.secret.replace(/(.{4})/g, "$1 ").trim()}
            </strong>
          </span>
          <TextField
            id="mfa-setup-code"
            label="Codul de 6 cifre (Parole → Coduri → Meniova Admin)"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "Se verifică…" : "Activează codul"}
          </button>
        </form>
      ) : (
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={busy}
          onClick={() => void start()}
        >
          {busy ? "Se pregătește…" : "Configurează codul de verificare"}
        </button>
      )}
    </AuthLayout>
  );
}

/** Guard: signed-in super admin on this host, else the admin sign-in. */
function AdminGuard() {
  const me = useAdminMe();
  if (me.isPending) {
    return (
      <div className="center-page" role="status">
        <span className="muted">Se încarcă…</span>
      </div>
    );
  }
  if (me.isError) {
    const status = me.error instanceof ApiRequestError ? me.error.status : 0;
    if (status === 401 || status === 404) return <Navigate to="/admin/login" replace />;
    if (me.error instanceof ApiRequestError && me.error.code === "mfa_required")
      return <Navigate to="/admin/login" replace />;
    return (
      <AuthLayout>
        <Alert tone="error" title="Administrarea nu a putut fi încărcată." live>
          {me.error.message}
        </Alert>
      </AuthLayout>
    );
  }
  if (me.data.mfaAvailable && !me.data.mfaEnabled) return <MfaSetup email={me.data.user.email} />;
  return <AdminShell email={me.data.user.email} mfaOff={!me.data.mfaAvailable} />;
}

function AdminLogin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  async function onCode(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setMessage("");
    try {
      await api<MeResponse>("POST", "/auth/mfa", { challenge, code: code.trim() });
      await qc.invalidateQueries({ queryKey: ["admin"] });
      navigate("/admin", { replace: true });
    } catch (err) {
      const f = apiFailure(err);
      setMessage(f.message);
      if (f.code === "mfa_expired") {
        setChallenge(null);
        setCode("");
      }
    } finally {
      setBusy(false);
    }
  }
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const v = validate(loginSchema, { email, password });
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    setErrors({});
    setBusy(true);
    setMessage("");
    try {
      const res = await api<MeResponse & { mfaRequired?: boolean; challenge?: string }>(
        "POST",
        "/auth/login",
        v.data,
      );
      if (res.mfaRequired && res.challenge) {
        setChallenge(res.challenge);
        return;
      }
      await qc.invalidateQueries({ queryKey: ["admin"] });
      navigate("/admin", { replace: true });
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <span className="eyebrow">Meniova</span>
        <h1 className="h1">Administrare</h1>
        <p className="muted" style={{ margin: 0 }}>
          Acces doar pentru administratorii Meniova.
        </p>
      </div>
      {message && (
        <Alert tone="error" live>
          <CircleAlert size={16} aria-hidden="true" /> {message}
        </Alert>
      )}
      {challenge ? (
        <form onSubmit={onCode} noValidate className="stack" style={{ gap: 16 }}>
          <TextField
            id="a-code"
            label="Codul de verificare (Parole → Coduri) sau un cod de recuperare"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button
            className="btn btn-primary btn-block"
            type="submit"
            disabled={busy || code.trim().length < 6}
          >
            {busy ? "Se verifică…" : "Verifică codul"}
          </button>
        </form>
      ) : (
        <form onSubmit={onSubmit} noValidate className="stack" style={{ gap: 16 }}>
          <TextField
            id="a-email"
            label="E-mail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <TextField
            id="a-password"
            label="Parola"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
          />
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "Se verifică…" : "Intră în administrare"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
/** The admin route tree (mounted under /admin). */
export const adminRoutes = [
  { path: "/admin/login", element: <AdminLogin /> },
  {
    path: "/admin",
    element: <AdminGuard />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: "restaurante", element: <Restaurants /> },
      { path: "restaurante/:id", element: <RestaurantDetail /> },
      { path: "abonamente", element: <Billing /> },
      { path: "importuri", element: <Imports /> },
      { path: "ai", element: <Ai /> },
      { path: "sistem", element: <System /> },
      { path: "chat", element: <ChatInbox /> },
      { path: "chat/:id", element: <ChatThread /> },
      { path: "suport", element: <Support /> },
      { path: "utilizatori", element: <Users /> },
      { path: "invitatii", element: <Invites /> },
      { path: "audit", element: <Audit /> },
    ],
  },
];
